/**
 * CSS-variable overrides for the immersive, full-bleed configurator presentation — shared by the
 * anonymous showroom (`ShowroomPage`) and the authenticated buyer immersive route
 * (`ImmersiveConfigurator`).
 *
 * The buyer flow's `BuyerWhiteLabelProvider` sets a battery of CSS variables that drive backgrounds,
 * content-card colors, header height, and active-tab fill. The showroom isn't behind that provider,
 * so without these neutral defaults the configurator looks bare (flat white, no card/segment colors,
 * missing active-tab background). Both immersive surfaces also zero out the app-shell spacing
 * (header, sidebar, content padding) so the configurator fills its container edge-to-edge, and
 * flatten/square the Rubus cards for the marketing-style full-bleed look.
 */
export const IMMERSIVE_STYLE_VARS: Record<string, string> = {
    '--cm-sidebar-width': '0px',
    '--cm-background-color': 'var(--rui-color-background, #eef1f3)',
    '--configurator-background': 'var(--rui-color-configurator-background, #eef1f3)',
    '--color-content-background': 'var(--rui-color-content-background, #fff)',
    '--buyer-max-content-width': '1600px',
    // Chromeless widget: no header, no app-shell content padding. Zero out the CSS variables the
    // global layout normally reserves for them so the widget fills its container edge-to-edge.
    '--cm-content-padding': '0px',
    '--cm-content-start-left': '0px',
    '--cm-buyer-header-height-override': '0px',
    '--cm-header-height': '0px',
    '--configurator-panel-top-position': '0px',
    // Full-bleed: remove the per-room left-panel inset entirely (`Room.module.scss` defaults to 32px)
    // so regions sit flush.
    '--cm-room-content-padding': '0px',
    // Remove the tab-bar's bottom margin so the row sits flush against the body.
    '--cm-tabs-container-margin-bottom': '0px',
    // Match the buyer flow's brand primary (the same purple used by the Sign-and-send buttons
    // elsewhere). BuyerWhiteLabelProvider uses theme.color.primary[500] -> var(--rui-color-primary-600).
    '--color-button': 'var(--rui-color-primary-600, var(--rui-color-text, #111))',
    '--color-button-focus': 'var(--rui-color-primary-700, var(--rui-color-text, #111))',
    '--color-button-active': 'var(--rui-color-primary-800, var(--rui-color-text, #111))',
    '--color-button-contrast': '#fff',
    // Flatten the Rubus Card shadows so cards don't visually float on the full-bleed surface.
    // Rubus Card uses `box-shadow: var(--card-shadow-variant, var(--card-shadow))` /
    // `--card-shadow: var(--rui-override-card-shadow, var(--rui-shadow-md))`, so overriding the
    // override var is enough.
    '--rui-override-card-shadow': 'none',
    '--rui-override-card-hover-shadow': 'none',
    // Square corners for the full-bleed layout (tab cards, panels, option cards).
    '--rui-override-card-border-radius': '0px',
};
